import type { JSX } from 'react';
import { cn } from '@/lib/utils';
import type { SavedInviteView } from '@/lib/moneyPages';
import type { InviteStageCounts } from '@/hooks/useAdmin';

const VIEWS: readonly { value: SavedInviteView; label: string; hint: string; count: keyof InviteStageCounts }[] = [
  {
    value: 'waiting',
    label: 'Replied, waiting 7+ days',
    hint: 'Logged more than a week ago and still waiting for the creator to join. Worth a nudge.',
    count: 'repliedOver7Days',
  },
  {
    value: 'joinedNoPost',
    label: 'Joined, no post yet',
    hint: 'Joined the clipping platform but no approved post yet.',
    count: 'joined',
  },
  {
    value: 'loggedThisWeek',
    label: 'Logged this week',
    hint: 'Every invite logged in the last 7 days, in any stage.',
    count: 'loggedLast7Days',
  },
];

/**
 * Saved views: the questions admins ask most, one click each. A view sets
 * the stage and date range; clicking the active one again clears it.
 * Counts are taken without the page's date range, since a view sets its own.
 */
export function InviteSavedViews({
  active,
  counts,
  onSelect,
}: {
  active: SavedInviteView | null;
  counts: InviteStageCounts | undefined;
  onSelect: (view: SavedInviteView) => void;
}): JSX.Element {
  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label="Saved views">
      {VIEWS.map((v) => {
        const isActive = active === v.value;
        const count = counts?.[v.count];
        return (
          <button
            key={v.value}
            type="button"
            aria-pressed={isActive}
            title={isActive ? `${v.hint} Click again to clear.` : v.hint}
            onClick={() => onSelect(v.value)}
            className={cn(
              'inline-flex items-baseline gap-2 rounded-lg border px-3 py-1.5 text-left text-sm transition-colors duration-[120ms]',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-1',
              isActive
                ? 'border-brand/40 bg-brand/10 text-foreground'
                : 'border-border bg-card text-muted-foreground hover:bg-accent/40 hover:text-foreground',
            )}
          >
            <span
              className={cn(
                'font-mono text-sm font-semibold tabular-nums',
                isActive ? 'text-foreground' : 'text-foreground/90',
              )}
            >
              {count === undefined ? '—' : count.toLocaleString()}
            </span>
            <span className="text-xs font-medium">{v.label}</span>
          </button>
        );
      })}
    </div>
  );
}
